'use client'

import { cn } from '@/_lib/utils'

import type { IAmbulanceCategory } from '@/_types'

export type AStepSelectAmbulanceListItemProps = {
	/** Category row from the tree; must expose `ambulance` to be clickable. */
	item: IAmbulanceCategory
	/** Fired on row activation (button is disabled when `item.ambulance` is missing). */
	onSelect: (item: IAmbulanceCategory) => void
	/** Extra classes, e.g. strip bottom border on the last cell of a grid row. */
	className?: string
}

/**
 * Single ambulance name row in the category group grid.
 *
 * @param item - {@link IAmbulanceCategory} leaf; rows without `ambulance` render disabled.
 * @param onSelect - Passed the same `item` when the user clicks an enabled row.
 * @param className - Merged into the row button for grid border tweaks.
 * @returns A list item with an accessible button and optional chevron affordance on hover (md+).
 */
export function AStepSelectAmbulanceListItem({
	item,
	onSelect,
	className
}: AStepSelectAmbulanceListItemProps) {
	return (
		<li className='min-w-0'>
			<button
				type='button'
				aria-label={item.name}
				disabled={!item.ambulance}
				onClick={() => onSelect(item)}
				className={cn(
					'group/amb-item flex w-full min-w-0 cursor-pointer items-start gap-3 text-left disabled:cursor-not-allowed disabled:opacity-40',
					'border-b h-full border-medante-purple-dark/20 md:border-none',
					'py-2.5 md:py-2.5',
					className
				)}
			>
				{/* Faux-bold via text-shadow avoids hover line-wrap from real font-weight changes. */}
				<span
					className={cn(
						'min-w-0 flex-1 text-base font-normal leading-normal text-purple',
						'transition-[text-shadow] duration-200 ease-out',
						'group-hover/amb-item:[text-shadow:0.015em_0_0_currentColor,-0.015em_0_0_currentColor]',
						'group-focus-visible/amb-item:[text-shadow:0.015em_0_0_currentColor,-0.015em_0_0_currentColor]'
					)}
				>
					{item.name}
				</span>

				<span
					aria-hidden
					className={cn(
						'pointer-events-none flex size-[28px] shrink-0 items-center justify-center rounded-full',
						'bg-white text-medante-purple-dark',
						'transition-colors duration-300 ease-out',
						'md:opacity-0 md:group-hover/amb-item:opacity-100'
					)}
				>
					<svg className='sico h-[10px] w-[10px] fill-current' aria-hidden>
						<use href='/assets/layout/icons.svg#s_ar2_r' />
					</svg>
				</span>
			</button>
		</li>
	)
}
